import { logger } from '@adornis/base/logging.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { mongo } from '@adornis/bh-util/server/mongo.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { addMinutes, differenceInMinutes } from 'date-fns';

@Entity()
export class ZoomMeetingOptions extends AdornisEntity {
  static override _class = 'ZoomMeetingOptions';
  @Field(type => String) topic!: string;
  @Field(type => Date) startTime?: Date;
  @Field(type => Date) endTime?: Date;
}

// accessToken is used to make requests on behalf of a user
// refreshToken has to be used to get a new accessToken when it expired (response may contain new refresh token)
@Entity()
export class ZoomUserAccessToken extends MongoEntity {
  static override _class = 'ZoomUserAccessToken';
  static override _collectionName = 'zoom-user-access-tokens';

  @Field(type => String) accessToken!: string;
  @Field(type => Date) expiresAt!: Date;
  @Field(type => String) refreshToken!: string;
  @Field(type => String) tokenType!: string;
  @Field(type => String) scope!: string;
  @Field(type => String) zoomUserID!: string; // this is not contained in the stripe auth response response, but convenient to have
}

export async function createInitalZoomAccessTokenFromAuthorizationCode(authorizationCode: string, redirectUri: string) {
  // import zoomConfig dynamically so that it is not bundled client side
  const { clientSecret, clientID } = await (async () => {
    const zoomConfig = await import('../zoom-config.js').then(m => m.zoomConfig);
    return { clientSecret: zoomConfig.get('clientSecret'), clientID: zoomConfig.get('clientID') };
  })();

  if (!clientID || !clientSecret) throw new Error('Zoom integration is not configured');

  // get access token — redirect_uri must match the one used in the authorize step byte-for-byte,
  // and is required whenever the app has more than one redirect URI registered
  const headers = new Headers();
  headers.set('Authorization', 'Basic ' + Buffer.from(clientID + ':' + clientSecret).toString('base64'));
  headers.set('Content-Type', 'application/x-www-form-urlencoded');
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code: authorizationCode,
    redirect_uri: redirectUri,
  }).toString();
  const authResponse = await fetch('https://zoom.us/oauth/token', { method: 'POST', headers, body });
  const authResponseBody = await authResponse.json();

  const { access_token: bearerToken } = authResponseBody;
  if (!bearerToken) throw new Error('could not authenticate');

  // get userID
  const userResponse = await fetch('https://api.zoom.us/v2/users/me', {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const userResponseBody = await userResponse.json();
  const zoomUserID = userResponseBody.id;
  if (!zoomUserID) throw new Error('could not get userID');

  const token = new ZoomUserAccessToken({
    accessToken: bearerToken,
    expiresAt: new Date(Date.now() + authResponseBody.expires_in * 1000),
    refreshToken: authResponseBody.refresh_token,
    tokenType: authResponseBody.token_type,
    scope: authResponseBody.scope,
    zoomUserID,
  });

  await token.create();

  return token._id;
}

async function refreshZoomUserAccessTokenIfExpired(
  zoomUserAccessToken: Pick<ZoomUserAccessToken, 'accessToken' | 'expiresAt' | 'refreshToken' | 'zoomUserID' | '_id'>,
) {
  // token is still valid (with 5 min buffer)
  if (zoomUserAccessToken.expiresAt.getTime() > addMinutes(new Date(), 5).getTime()) {
    return;
  }

  // renew token
  // import zoom config dynamically so that it is not bundled client side
  const { clientSecret, clientID } = await (async () => {
    const zoomConfig = await import('../zoom-config.js').then(m => m.zoomConfig);
    return { clientSecret: zoomConfig.get('clientSecret'), clientID: zoomConfig.get('clientID') };
  })();

  if (!clientID || !clientSecret) throw new Error('Zoom integration is not configured');

  // prepare request to refresh token
  const headers = new Headers();
  headers.set('Authorization', 'Basic ' + Buffer.from(clientID + ':' + clientSecret).toString('base64'));
  headers.set('Content-Type', 'application/x-www-form-urlencoded');
  const body = `grant_type=refresh_token&refresh_token=${zoomUserAccessToken.refreshToken}`;

  // make request to refresh token
  const refreshResponse = await fetch('https://zoom.us/oauth/token', { method: 'POST', headers, body });

  if (!refreshResponse.ok) {
    const errorData = await refreshResponse.json();
    logger.error({ errorData }, 'Failed to refresh Zoom token');
    throw new Error(`Failed to refresh Zoom token: ${errorData.error || 'Unknown error'}`);
  }

  const refreshResponseBody = await refreshResponse.json();
  logger.info({ refreshResponseBody }, 'Received new Zoom tokens');

  // get required values from response
  const {
    access_token: newAccessToken,
    refresh_token: newRefreshToken,
    expires_in: newExpiresIn,
  } = refreshResponseBody;

  if (!newAccessToken) {
    throw new Error('No access token in refresh response');
  }

  // update token — via raw collection, since tokens are server-only secrets without entityPermissions
  // (a .save() inside a mutation context fails the baseql permission check)
  zoomUserAccessToken.accessToken = newAccessToken;
  zoomUserAccessToken.expiresAt = new Date(Date.now() + newExpiresIn * 1000);
  zoomUserAccessToken.refreshToken = newRefreshToken || zoomUserAccessToken.refreshToken;
  const tokenCollection = mongo().collection<ZoomUserAccessToken>(ZoomUserAccessToken._collectionName);
  await tokenCollection.updateOne(
    { _id: zoomUserAccessToken._id },
    {
      $set: {
        accessToken: zoomUserAccessToken.accessToken,
        expiresAt: zoomUserAccessToken.expiresAt,
        refreshToken: zoomUserAccessToken.refreshToken,
      },
    },
  );
  logger.info({ tokenId: zoomUserAccessToken._id }, 'Successfully refreshed and saved Zoom token');
}

export const createZoomMeetingForUser = registerMutation({
  operationName: 'createZoomMeetingForUser',
  params: [
    { name: 'zoomAccessTokenID', type: () => String },
    { name: 'options', type: () => ZoomMeetingOptions },
  ],
  type: () => String, // MeetingLink
  resolve: (zoomAccessTokenID: string, options: ZoomMeetingOptions) => {
    return async () => {
      if (context.serverContext || !context.userID) throw new Error('Not authenticated');
      const userID = context.userID;
      const userCollection = mongo().collection<{
        _id: string;
        zoomAccessTokenID?: string;
        roles?: { name: string; contexts?: string[] }[];
      }>('adornisuser');
      const ownToken = await userCollection.findOne({ _id: userID, zoomAccessTokenID }, { projection: { _id: 1 } });
      if (!ownToken) {
        const caller = await getCurrentUserServer();
        let authorized = !!caller?.isSuperAdmin;
        if (caller && !authorized) {
          const tokenOwner = await userCollection.findOne(
            { zoomAccessTokenID },
            { projection: { _id: 1, roles: { name: 1, contexts: 1 } } },
          );
          if (tokenOwner) {
            // Keep these values aligned with buchungsheld-db/enums/roles.ts without adding a dependency on db.
            const canEdit = (institutionID: string) =>
              caller.hasPermission('edit-admin-institution-workshops', institutionID) ||
              caller.hasPermission('edit-admin-institution-counselings', institutionID);
            const membershipRoles = (tokenOwner.roles ?? []).filter(
              role => role.name === 'lecturer' || role.name === 'berater',
            );
            // Lecturer checks in db also recognize roles without contexts as institution-wide membership.
            const globalMembership = membershipRoles.some(
              role => role.contexts?.includes('GLOBAL') || (role.name === 'lecturer' && !role.contexts?.length),
            );
            const membershipContexts = membershipRoles.flatMap(role => role.contexts ?? []);
            // Berater contexts also contain counseling IDs; only actual institutions qualify here.
            const institutions = await mongo()
              .collection<{ _id: string }>('institutions')
              .find(globalMembership ? {} : { _id: { $in: membershipContexts } }, { projection: { _id: 1 } })
              .toArray();
            authorized = institutions.some(institution => canEdit(institution._id));
            if (!authorized) {
              // Saved assignments are authoritative even before the derived Berater role is refreshed.
              const counselings = await mongo()
                .collection<{ _id: string; institutionID: string }>('products')
                .find(
                  {
                    _class: 'Einzelberatung',
                    $or: [
                      { 'slotConfig.beraterIDs': tokenOwner._id },
                      { 'slots.beraterID': tokenOwner._id },
                      { 'beraterAvailabilities.beraterID': tokenOwner._id },
                    ],
                  },
                  { projection: { institutionID: 1 } },
                )
                .toArray();
              authorized = counselings.some(
                counseling => !!counseling.institutionID && canEdit(counseling.institutionID),
              );
            }
          }
        }
        if (!authorized) throw new Error('Zoom access token does not belong to the caller');
      }

      const { startTime, endTime } = options;
      const duration = startTime && endTime ? differenceInMinutes(endTime, startTime) : undefined;

      if (startTime && startTime.getTime() < Date.now()) {
        throw new Error('Das Startdatum muss in der Zukunft liegen');
      }

      // get auth token
      const tokenCollection = mongo().collection<ZoomUserAccessToken>(ZoomUserAccessToken._collectionName);
      const zoomUserAccessToken = await tokenCollection.findOne(
        { _id: zoomAccessTokenID },
        { projection: { accessToken: 1, refreshToken: 1, expiresAt: 1, zoomUserID: 1, _id: 1 } },
      );
      if (!zoomUserAccessToken) throw new Error('Zoom access token not found ' + zoomAccessTokenID);
      await refreshZoomUserAccessTokenIfExpired(zoomUserAccessToken);

      const url = `https://api.zoom.us/v2/users/${zoomUserAccessToken.zoomUserID}/meetings`;
      const zoomOptions = {
        topic: options.topic.slice(0, 200), // zoom rejects topics longer than 200 chars
        type: 2,
        // start_time stays an absolute UTC instant (zoom documents yyyy-MM-ddTHH:mm:ssZ, without milliseconds);
        // timezone only sets how zoom displays the meeting, otherwise it falls back to the host's profile timezone
        start_time: startTime?.toISOString().replace(/\.\d{3}Z$/, 'Z'),
        timezone: 'Europe/Berlin',
        duration,
        settings: {
          host_video: true,
          participant_video: false,
          join_before_host: false,
          mute_upon_entry: true,
          watermark: false,
          use_pmi: false,
          approval_type: 0,
          registration_type: 1,
          audio: 'both',
          auto_recording: 'none',
          waiting_room: true,
        },
      };

      logger.info({ zoomOptions }, 'creating zoom meeting');
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${zoomUserAccessToken.accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(zoomOptions),
      });

      const responseJSON = await response.json().catch(() => ({}));
      if (!response.ok || !responseJSON.join_url) {
        logger.error({ status: response.status, responseJSON }, 'failed to create zoom meeting');
        throw new Error(responseJSON.message || `Zoom hat die Anfrage abgelehnt (${response.status})`);
      }
      logger.info({ responseJSON }, 'created zoom meeting');
      const { join_url: meetingLink } = responseJSON;

      return meetingLink;
    };
  },
});
